import { useState } from 'react';
import {
  Card, Button, Typography, Space, Tag, Row, Col, Alert, Divider,
} from 'antd';
import {
  ArrowLeftOutlined,
  InstagramOutlined,
  VideoCameraOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { SocialPost } from 'services/social.service';
import { usePublishSocialPost } from 'hooks/api/useSocialPost';

const { Text, Paragraph, Title } = Typography;

const InstagramMockup = ({ image, caption, productName }: { image: string; caption: string; productName: string }) => (
  <div style={{
    width: '100%',
    maxWidth: 360,
    border: '1px solid #dbdbdb',
    borderRadius: 8,
    background: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    overflow: 'hidden',
  }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px' }}>
      <div style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
      }}
      />
      <Text strong style={{ fontSize: 13 }}>bibikalka.com.ua</Text>
    </div>

    <div style={{ width: '100%', aspectRatio: '1', overflow: 'hidden', background: '#f0f0f0' }}>
      <img
        src={image}
        alt={productName}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>

    <div style={{ padding: '12px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <Space size={14}>
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <path d='M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z' />
          </svg>
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
          </svg>
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <line x1='22' y1='2' x2='11' y2='13' />
            <polygon points='22 2 15 22 11 13 2 9 22 2' />
          </svg>
        </Space>
        <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
          <path d='M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z' />
        </svg>
      </div>
      <Text strong style={{ fontSize: 13 }}>bibikalka.com.ua</Text>
      {' '}
      <Text style={{ fontSize: 13 }}>
        {caption.slice(0, 120)}
        {caption.length > 120 ? '...' : ''}
      </Text>
    </div>
  </div>
);

const TikTokMockup = ({ image, caption, productName }: { image: string; caption: string; productName: string }) => (
  <div style={{
    width: '100%',
    maxWidth: 220,
    background: '#000',
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
    aspectRatio: '9/16',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  }}
  >
    <img
      src={image}
      alt={productName}
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', position: 'absolute', inset: 0 }}
    />
    <div style={{
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)',
    }}
    />

    <div style={{ position: 'absolute', bottom: 80, left: 12, right: 48 }}>
      <Text strong style={{ color: '#fff', fontSize: 12, display: 'block', marginBottom: 6 }}>
        @bibikalka.com.ua
      </Text>
      <Text style={{ color: '#fff', fontSize: 11, lineHeight: '1.4' }}>
        {caption.slice(0, 80)}
        {caption.length > 80 ? '...' : ''}
      </Text>
    </div>

    <div style={{
      position: 'absolute',
      right: 8,
      bottom: 80,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 16,
    }}
    >
      {[
        { icon: '❤️', label: '0' },
        { icon: '💬', label: '0' },
        { icon: '↗️', label: '0' },
      ].map(({ icon, label }) => (
        <div key={icon} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 22 }}>{icon}</div>
          <Text style={{ color: '#fff', fontSize: 10 }}>{label}</Text>
        </div>
      ))}
    </div>

    <div style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      padding: '12px 16px',
      background: 'rgba(0,0,0,0.4)',
    }}
    >
      <div style={{ display: 'flex', gap: 16 }}>
        {['Для вас', 'Підписки'].map((label, i) => (
          <Text key={label} style={{ color: i === 0 ? '#fff' : 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: i === 0 ? 700 : 400 }}>
            {label}
          </Text>
        ))}
      </div>
    </div>
  </div>
);

const PlatformStatus = ({ platform, label }: { platform: SocialPost['instagram'] | SocialPost['tiktok']; label: string }) => {
  if (!platform.enabled) return null;
  return (
    <Space>
      <Text strong>
        {label}
        :
      </Text>
      {platform.publishStatus === 'success' && (
        <Tag icon={<CheckCircleOutlined />} color='success'>Опубліковано</Tag>
      )}
      {platform.publishStatus === 'failed' && (
        <Tag icon={<CloseCircleOutlined />} color='error'>
          Помилка
          {platform.error ? `: ${platform.error}` : ''}
        </Tag>
      )}
      {!platform.publishStatus && <Tag color='default'>Очікує публікації</Tag>}
    </Space>
  );
};

interface PostPreviewProps {
  post: SocialPost;
  onBack?: () => void;
  onPublished?: (updated: SocialPost) => void;
  readOnly?: boolean;
}

const PostPreview = ({ post, onBack, onPublished, readOnly = false }: PostPreviewProps) => {
  const [currentPost, setCurrentPost] = useState(post);
  const { mutate: publish, isLoading } = usePublishSocialPost();

  const handlePublish = () => {
    publish(currentPost._id, {
      onSuccess: (updated) => {
        setCurrentPost(updated);
        onPublished?.(updated);
      },
    });
  };

  const isPublished = currentPost.status === 'published';
  const hasEnabledPlatforms = currentPost.instagram.enabled || currentPost.tiktok.enabled;

  return (
    <Card>
      <Space style={{ marginBottom: 16 }}>
        {onBack && (
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            Назад
          </Button>
        )}
        <Title level={5} style={{ margin: 0 }}>{currentPost.productName}</Title>
        {isPublished
          ? <Tag color='success'>Опубліковано</Tag>
          : <Tag color='orange'>Чернетка</Tag>}
      </Space>

      <Row gutter={[24, 24]} justify='center'>
        {currentPost.instagram.enabled && (
          <Col xs={24} sm={12} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <Space>
              <InstagramOutlined style={{ color: '#e1306c', fontSize: 16 }} />
              <Text strong>Instagram</Text>
            </Space>
            <InstagramMockup
              image={currentPost.enhancedImage}
              caption={currentPost.caption}
              productName={currentPost.productName}
            />
          </Col>
        )}

        {currentPost.tiktok.enabled && (
          <Col xs={24} sm={12} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <Space>
              <VideoCameraOutlined style={{ fontSize: 16 }} />
              <Text strong>TikTok</Text>
            </Space>
            <TikTokMockup
              image={currentPost.enhancedImage}
              caption={currentPost.caption}
              productName={currentPost.productName}
            />
          </Col>
        )}
      </Row>

      <Divider />

      <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
        <Text type='secondary' style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>Текст публікації</Text>
        <Paragraph style={{ margin: 0 }}>{currentPost.caption}</Paragraph>
      </div>

      {isPublished && (
        <Space direction='vertical' style={{ width: '100%', marginBottom: 16 }}>
          <PlatformStatus platform={currentPost.instagram} label='Instagram' />
          <PlatformStatus platform={currentPost.tiktok} label='TikTok' />
        </Space>
      )}

      {!isPublished && !readOnly && hasEnabledPlatforms && (
        <Alert
          type='info'
          message='Готово до публікації'
          description='Перевірте превʼю вище та натисніть "Опублікувати" щоб розмістити пост.'
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {!isPublished && !readOnly && (
        <Button
          type='primary'
          size='large'
          icon={<SendOutlined />}
          loading={isLoading}
          onClick={handlePublish}
          disabled={!hasEnabledPlatforms}
          block
        >
          {isLoading ? 'Публікуємо...' : 'Опублікувати'}
        </Button>
      )}
    </Card>
  );
};

export default PostPreview;
